import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  useJiraIssues,
  useJiraActions,
  useJiraTransitions,
  useJiraStatuses,
  JiraIssue,
  JiraStatusEntry
} from '../../hooks/useJiraTasks';
import { useJiraTaskTimes } from '../../hooks/useJiraTaskTimes';
import { useJiraConnection } from '../../hooks/useJiraConnection';
import { useJiraBoardConfig, JiraBoardColumn } from '../../hooks/useJiraBoardConfig';
import { formatDuration } from '../../utils/timeUtils';
import JiraConnectModal from './JiraConnectModal';
import JiraBoardConfigModal from './JiraBoardConfigModal';
import './JiraPanel.css';

interface JiraPanelProps {
  /** Intranet employee id — passed through to the time-tracking hook. */
  userId?: string;
  /** Whether the viewer is an admin (can open the connection wizard). */
  isAdmin?: boolean;
  /** Called when the user clicks "Start in eDoss" with the bridge seed. */
  onStartInEdoss?: (issueKey: string) => void;
}

/**
 * Map a Jira statusCategory (the only status grouping Jira's API exposes) to
 * a board tone. Jira guarantees one of: "To Do", "In Progress", "Done".
 */
const categoryTone = (statusCategory: string): string => {
  const cat = String(statusCategory || '').toLowerCase().trim();
  if (cat === 'in progress') return 'progress';
  if (cat === 'done') return 'done';
  if (cat === 'to do') return 'todo';
  return 'neutral';
};

/**
 * Resolve the tone for a transition's target status name via the backend
 * status map (name → statusCategory). Falls back to neutral when the map
 * hasn't loaded or the status isn't known.
 */
const transitionTone = (
  toStatus: string,
  statusMap: Record<string, JiraStatusEntry>
): string => {
  const entry = statusMap[String(toStatus || '').toLowerCase().trim()];
  return categoryTone(entry?.statusCategory || '');
};

/** Neutral placeholder badge for an issue type missing its Jira icon URL. */
const fallbackTypeBadge = (issueType: string): { letter: string } => ({
  letter: String(issueType || '?').trim().charAt(0).toUpperCase() || '?'
});

/** Fallback columns when no admin board config is saved — the 3 Jira categories. */
const DEFAULT_COLUMNS: EffectiveColumn[] = [
  { id: 'progress', title: 'In Progress', tone: 'progress' },
  { id: 'todo', title: 'To Do', tone: 'todo' },
  { id: 'done', title: 'Done', tone: 'done' }
];

const OTHER_COLUMN: EffectiveColumn = { id: '__other__', title: 'Other', tone: 'neutral' };

interface EffectiveColumn {
  id: string;
  title: string;
  tone: string;
  items?: JiraIssue[];
}

const JiraPanel: React.FC<JiraPanelProps> = ({ userId, isAdmin, onStartInEdoss }) => {
  const {
    issues,
    loading,
    error,
    configured,
    refetch
  } = useJiraIssues();
  const { settings } = useJiraConnection();

  const { busyKey, transition } = useJiraActions();
  const { fetchTimes, getTime } = useJiraTaskTimes(userId);
  const { statusMap } = useJiraStatuses();
  const { columns: boardColumns, refetch: refetchBoardConfig } = useJiraBoardConfig();

  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [boardCfgOpen, setBoardCfgOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { transitions: expandedTransitions, loading: transitionsLoading } =
    useJiraTransitions(expandedKey);

  const handleConnected = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleBoardSaved = useCallback(() => {
    refetchBoardConfig();
    refetch();
  }, [refetchBoardConfig, refetch]);

  useEffect(() => {
    if (issues.length) {
      fetchTimes(issues.map((i) => i.key));
    }
  }, [issues, fetchTimes]);

  const handleTransition = useCallback(
    async (issue: JiraIssue, status: string) => {
      try {
        await transition(issue.key, status);
        refetch();
      } catch (err: any) {
        console.error('Failed to transition Jira issue:', err);
      }
    },
    [transition, refetch]
  );

  const filteredIssues = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return issues;
    return issues.filter((i) => {
      return (
        String(i.key || '').toLowerCase().includes(q) ||
        String(i.summary || '').toLowerCase().includes(q) ||
        String(i.projectKey || '').toLowerCase().includes(q) ||
        String(i.projectName || '').toLowerCase().includes(q) ||
        String(i.status || '').toLowerCase().includes(q) ||
        String(i.issueType || '').toLowerCase().includes(q)
      );
    });
  }, [issues, query]);

  const stats = useMemo(() => {
    const count = (tones: string[]) =>
      issues.filter((i) => tones.includes(categoryTone(i.statusCategory))).length;
    return {
      total: issues.length,
      inProgress: count(['progress']),
      todo: count(['todo']),
      done: count(['done'])
    };
  }, [issues]);

  const columns = useMemo(() => {
    const cfg = boardColumns && boardColumns.length ? boardColumns : null;
    const defs: EffectiveColumn[] = cfg
      ? cfg.map((c) => ({ id: c.id, title: c.title, tone: c.tone }))
      : DEFAULT_COLUMNS;

    const buckets: Record<string, JiraIssue[]> = {};
    for (const d of defs) buckets[d.id] = [];
    buckets[OTHER_COLUMN.id] = [];

    for (const issue of filteredIssues) {
      let targetId: string | null = null;
      if (cfg) {
        const statusLower = String(issue.status || '').toLowerCase().trim();
        const match = cfg.find((c) =>
          c.statusNames.some((n) => String(n || '').toLowerCase().trim() === statusLower)
        );
        if (match) targetId = match.id;
      } else {
        const tone = categoryTone(issue.statusCategory);
        const def = defs.find((d) => d.tone === tone);
        if (def) targetId = def.id;
      }
      if (targetId && buckets[targetId]) buckets[targetId].push(issue);
      else buckets[OTHER_COLUMN.id].push(issue);
    }

    const result = defs.map((d) => ({ ...d, items: buckets[d.id] }));
    const otherItems = buckets[OTHER_COLUMN.id];
    if (otherItems.length) result.push({ ...OTHER_COLUMN, items: otherItems });
    return result.filter((c) => c.items.length > 0);
  }, [filteredIssues, boardColumns]);

  const renderCard = (issue: JiraIssue, tone: string) => {
    const isBusy = busyKey === issue.key;
    const isExpanded = expandedKey === issue.key;
    const localSeconds = getTime(issue.key);
    const fallback = fallbackTypeBadge(issue.issueType);
    const iconSrc = String(issue.issueTypeIcon || '').trim();
    const transitions = isExpanded ? expandedTransitions : [];
    const transitionsBusy = isBusy;

    return (
      <div
        key={issue.key}
        className={`jira-card tone-${tone}${isExpanded ? ' expanded' : ''}`}
      >
        <div
          className="jira-card-head"
          onClick={() => setExpandedKey(isExpanded ? null : issue.key)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpandedKey(isExpanded ? null : issue.key);
            }
          }}
        >
          <div className="jira-card-head-top">
            {iconSrc ? (
              <img
                className="jira-type-badge jira-type-badge-icon"
                src={iconSrc}
                alt={issue.issueType || ''}
                title={issue.issueType || ''}
              />
            ) : (
              <span className="jira-type-badge" title={issue.issueType}>
                {fallback.letter}
              </span>
            )}
            <span className="jira-card-key">{issue.key}</span>
            {issue.issueType && (
              <span className="jira-chip jira-chip-type">{issue.issueType}</span>
            )}
          </div>
          <div className="jira-card-summary" title={issue.summary || ''}>
            {issue.summary || 'untitled'}
          </div>
          <div className="jira-card-foot">
            {issue.projectKey && (
              <span className="jira-chip jira-chip-project">{issue.projectKey}</span>
            )}
            {localSeconds > 0 && (
              <span className="jira-card-time" title="Local tracked time">
                {formatDuration(localSeconds)}
              </span>
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="jira-card-actions">
            <div className="jira-issue-action-buttons">
              {transitionsLoading && (
                <span className="jira-transitions-muted">Loading transitions…</span>
              )}
              {!transitionsLoading && transitions.length === 0 && (
                <span className="jira-transitions-muted">No workflow transitions available.</span>
              )}
              {!transitionsLoading &&
                transitions.map((t) => {
                  const toTone = transitionTone(t.toStatus, statusMap);
                  const isPrimary = toTone === 'done';
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={`jira-btn ${isPrimary ? 'jira-btn-primary' : 'jira-btn-secondary'}`}
                      disabled={transitionsBusy}
                      onClick={() => handleTransition(issue, t.toStatus)}
                      title={t.toStatus}
                    >
                      {t.name}
                    </button>
                  );
                })}
              {onStartInEdoss && (
                <button
                  type="button"
                  className="jira-btn jira-btn-accent"
                  disabled={isBusy}
                  onClick={() => onStartInEdoss(issue.key)}
                >
                  Start in eDoss
                </button>
              )}
              {settings?.siteUrl && (
                <a
                  className="jira-btn jira-btn-link"
                  href={`${settings.siteUrl}/browse/${issue.key}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Detail
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (configured === false) {
    return (
      <div className="jira-panel">
        <JiraConnectModal
          open={connectOpen}
          onClose={() => setConnectOpen(false)}
          onConnected={handleConnected}
        />
        <div className="jira-hero">
          <div className="jira-brand">
            <div className="jira-brand-mark" aria-hidden="true" />
            <div className="jira-brand-text">
              <h1>Jira</h1>
              <p>Not connected yet.</p>
            </div>
          </div>
        </div>
        <div className="jira-connect-cta">
          {isAdmin ? (
            <>
              <p>Connect your Atlassian account to pull assigned issues into eDoss.</p>
              <button
                type="button"
                className="jira-btn jira-btn-primary jira-btn-lg"
                onClick={() => setConnectOpen(true)}
              >
                Connect Jira
              </button>
            </>
          ) : (
            <p>Jira isn&apos;t configured. Ask an admin to connect it from this page.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="jira-panel">
      <JiraConnectModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        onConnected={handleConnected}
      />
      <JiraBoardConfigModal
        open={boardCfgOpen}
        onClose={() => setBoardCfgOpen(false)}
        onSaved={handleBoardSaved}
      />

      <div className="jira-hero">
        <div className="jira-brand">
          <div className="jira-brand-mark" aria-hidden="true" />
          <div className="jira-brand-text">
            <h1>Jira</h1>
            <p>
              {settings?.configured ? (
                <>
                  <span className="jira-status-dot connected" />
                  {settings.siteUrl}
                  {settings.email ? ` · ${settings.email}` : ''}
                </>
              ) : (
                'Your assigned open issues, grouped by status.'
              )}
            </p>
          </div>
        </div>

        <div className="jira-hero-actions">
          {isAdmin && (
            <button
              type="button"
              className="jira-settings-btn"
              onClick={() => setBoardCfgOpen(true)}
              title="Configure board columns"
            >
              ▤ Columns
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="jira-settings-btn"
              onClick={() => setConnectOpen(true)}
              title="Jira connection settings"
            >
              ⚙ Settings
            </button>
          )}
          <button
            type="button"
            className="jira-refresh"
            onClick={refetch}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="jira-toolbar">
        <div className="jira-stats">
          <div className="jira-stat">
            <span className="jira-stat-num">{stats.total}</span>
            <span className="jira-stat-label">Total</span>
          </div>
          <div className="jira-stat tone-progress">
            <span className="jira-stat-num">{stats.inProgress}</span>
            <span className="jira-stat-label">In Progress</span>
          </div>
          <div className="jira-stat tone-todo">
            <span className="jira-stat-num">{stats.todo}</span>
            <span className="jira-stat-label">To Do</span>
          </div>
          <div className="jira-stat tone-done">
            <span className="jira-stat-num">{stats.done}</span>
            <span className="jira-stat-label">Done</span>
          </div>
        </div>
        <div className="jira-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="jira-search-icon">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by key, summary, project, status…"
            aria-label="Search Jira issues"
          />
          {query && (
            <button type="button" className="jira-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
              ×
            </button>
          )}
        </div>
      </div>

      {error && <div className="jira-panel-error">{error}</div>}

      {!error && !loading && filteredIssues.length === 0 && (
        <div className="jira-panel-empty">
          {issues.length === 0
            ? 'No assigned open Jira issues right now.'
            : `No issues match "${query}".`}
        </div>
      )}

      {loading && issues.length === 0 && (
        <div className="jira-panel-empty">Loading Jira issues…</div>
      )}

      {columns.length > 0 && (
        <div className="jira-board">
          {columns.map((col) => (
            <div key={col.id} className="jira-column">
              <div className="jira-column-header">
                <span className={`jira-column-dot tone-${col.tone}`} />
                <span className="jira-column-title">{col.title}</span>
                <span className="jira-column-count">{col.items.length}</span>
              </div>
              <div className="jira-column-body">
                {col.items.map((issue) => renderCard(issue, col.tone))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default JiraPanel;
